import { FC, ReactNode } from 'react';
import { Button, Divider, Space, Typography } from 'antd';
import { ListFilter } from 'lucide-react';
import { TbCalendarTime, TbSend } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AlertIcon } from '@/shared/components/alerts/AlertIcons';
import { PreferenceModalIconAvatar } from '@/shared/components/modals/PreferenceModalPrimitives';

import { AlertRequiredField, alertRequiredFieldDetails } from '../../requiredFields';

export type AlertFlowNodeKind = 'alert' | 'condition' | 'delivery' | 'schedule';

export interface AlertFlowNodeMetadata {
    label: string;
    value: string;
}

export interface AlertFlowNodeDetail {
    description: string;
    id: string;
    kind: AlertFlowNodeKind;
    label: string;
    metadata: AlertFlowNodeMetadata[];
    requiredFields: AlertRequiredField[];
    title: string;
}

interface AlertFlowNodeInspectorProps {
    node?: AlertFlowNodeDetail;
    onSelectRequiredField?: (field: AlertRequiredField) => void;
}

export const AlertFlowNodeIcon: FC<{ kind: AlertFlowNodeKind }> = ({ kind }) => {
    const icons: Record<AlertFlowNodeKind, ReactNode> = {
        alert: <AlertIcon />,
        condition: <ListFilter size='0.875rem' />,
        delivery: <TbSend />,
        schedule: <TbCalendarTime />,
    };

    return icons[kind];
};

const AlertFlowNodeInspector: FC<AlertFlowNodeInspectorProps> = ({ node, onSelectRequiredField }) => {
    const { token } = useAntDesignTheme();

    if (!node) {
        return (
            <div style={{ padding: '1rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Workflow Details</small>
                <Typography.Paragraph style={{ color: token.colorTextSecondary, margin: '0.75rem 0 0' }}>
                    Select a workflow node to inspect its configuration.
                </Typography.Paragraph>
            </div>
        );
    }

    return (
        <div style={{ height: '100%', overflow: 'auto', padding: '1rem' }}>
            <small style={{ color: token.colorTextTertiary }}>Workflow Details</small>
            <Space align='start' size={10} style={{ marginTop: '0.75rem', width: '100%' }}>
                <PreferenceModalIconAvatar icon={<AlertFlowNodeIcon kind={node.kind} />} />
                <div style={{ minWidth: 0 }}>
                    <small style={{ color: token.colorTextTertiary }}>{node.label}</small>
                    <Typography.Title level={5} style={{ margin: '0.125rem 0 0' }}>
                        {node.title}
                    </Typography.Title>
                </div>
            </Space>
            {node.description ? (
                <Typography.Paragraph style={{ color: token.colorTextSecondary, margin: '0.75rem 0 0' }}>
                    {node.description}
                </Typography.Paragraph>
            ) : null}

            {node.metadata.length ? (
                <>
                    <Divider style={{ margin: '1rem 0' }} />
                    <small style={{ color: token.colorTextTertiary }}>Node Configuration</small>
                    <div style={{ marginTop: '0.5rem' }}>
                        {node.metadata.map((entry, index) => (
                            <div
                                key={entry.label}
                                style={{
                                    alignItems: 'flex-start',
                                    borderBottom:
                                        index === node.metadata.length - 1
                                            ? undefined
                                            : `1px solid ${token.colorBorderSecondary}`,
                                    display: 'flex',
                                    gap: '0.75rem',
                                    justifyContent: 'space-between',
                                    padding: '0.625rem 0',
                                }}
                            >
                                <small style={{ color: token.colorTextTertiary }}>{entry.label}</small>
                                <small style={{ color: token.colorText, textAlign: 'right' }}>{entry.value}</small>
                            </div>
                        ))}
                    </div>
                </>
            ) : null}

            {node.requiredFields.length ? (
                <>
                    <Divider style={{ margin: '1rem 0' }} />
                    <small style={{ color: token.colorTextTertiary }}>Required Configuration</small>
                    <Space direction='vertical' size={4} style={{ marginTop: '0.5rem', width: '100%' }}>
                        {node.requiredFields.map((field) => (
                            <Button
                                key={field}
                                block
                                size='small'
                                style={{ textAlign: 'left' }}
                                onClick={() => onSelectRequiredField?.(field)}
                            >
                                {alertRequiredFieldDetails[field].label}
                            </Button>
                        ))}
                    </Space>
                </>
            ) : null}
        </div>
    );
};

export default AlertFlowNodeInspector;
